import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import prefersReducedMotion from '../utils/prefers-reduced-motion';

gsap.registerPlugin(Draggable, InertiaPlugin);

/**
 * Timeline (wheel)
 */

const SELECTOR = '.timeline';
const DEFAULT_STEP = 22; // deg between dates; kept in sync with --tl-step in the SCSS.

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const initWheel = (root) => {
	if (root.dataset.timelineBound === 'true') {
		return;
	}
	root.dataset.timelineBound = 'true';

	const stage = root.querySelector('.timeline__stage');
	const ring = root.querySelector('.timeline__ring');
	const items = Array.from(root.querySelectorAll('.timeline__item'));
	const copyItems = Array.from(root.querySelectorAll('.timeline__copy-item'));
	const prevBtn = root.querySelector('.timeline__arrow--prev');
	const nextBtn = root.querySelector('.timeline__arrow--next');
	const centre = root.querySelector('.timeline__centre');

	const setLive = (on) => {
		if (centre) {
			centre.setAttribute('aria-live', on ? 'polite' : 'off');
		}
	};

	const count = items.length;
	if (!ring || count < 2) {
		return;
	}

	const stepDeg =
		parseFloat(
			window.getComputedStyle(root).getPropertyValue('--tl-step')
		) || DEFAULT_STEP;

	const autoplay = root.dataset.autoplay === '1';
	const speed = parseFloat(root.dataset.speed) || 5;
	const desktopMQ = window.matchMedia('(min-width: 992px)');

	const rotationFor = (index) => -index * stepDeg;
	const indexFromRotation = (rotation) =>
		clamp(Math.round(-rotation / stepDeg), 0, count - 1);
	// Bounds keep the wheel between the first (rotation 0) and last date.
	const minRotation = rotationFor(count - 1);
	const maxRotation = rotationFor(0);

	let current = 0;
	let direction = 1; // Autoplay travel direction; flips at either end.
	let timer = null;
	let isPointerOver = false;
	let isFocused = false;
	let isDragging = false;
	let transitionTl = null;

	const stopTimer = () => {
		if (timer) {
			timer.kill();
			timer = null;
		}
	};

	const startTimer = () => {
		if (
			!autoplay ||
			isPointerOver ||
			isFocused ||
			isDragging ||
			prefersReducedMotion() ||
			!desktopMQ.matches
		) {
			return;
		}
		stopTimer();
		timer = gsap.delayedCall(speed, () => {
			let next = current + direction;
			if (next > count - 1) {
				direction = -1;
				next = current + direction;
			} else if (next < 0) {
				direction = 1;
				next = current + direction;
			}
			setLive(false); // autoplay must not announce
			goTo(next);
			startTimer();
		});
	};

	const updateButtons = () => {
		const atStart = current <= 0;
		const atEnd = current >= count - 1;
		if (prevBtn) {
			if (atStart && !atEnd && nextBtn && document.activeElement === prevBtn) {
				nextBtn.focus();
			}
			prevBtn.disabled = atStart;
		}
		if (nextBtn) {
			if (atEnd && !atStart && prevBtn && document.activeElement === nextBtn) {
				prevBtn.focus();
			}
			nextBtn.disabled = atEnd;
		}
	};

	const applyActive = (index, { animate = true } = {}) => {
		const next = clamp(index, 0, count - 1);
		const previous = current;
		if (next === previous && animate) {
			return;
		}
		current = next;

		items.forEach((item, i) =>
			item.classList.toggle('is-active', i === current)
		);
		copyItems.forEach((item, i) =>
			item.classList.toggle('is-active', i === current)
		);
		updateButtons();

		const shouldAnimate = animate && !prefersReducedMotion();

		if (!shouldAnimate) {
			if (transitionTl) {
				transitionTl.kill();
				transitionTl = null;
			}
			copyItems.forEach((item, i) =>
				gsap.set(item, { autoAlpha: i === current ? 1 : 0, y: 0 })
			);
			return;
		}

		if (transitionTl) {
			transitionTl.kill();
		}
		transitionTl = gsap.timeline({
			defaults: { ease: 'power2.out' },
			onComplete: () => {
				transitionTl = null;
			},
		});

		copyItems.forEach((item, i) => {
			if (i !== current && i !== previous) {
				gsap.set(item, { autoAlpha: 0 });
			}
		});

		if (previous !== current && copyItems[previous]) {
			transitionTl.to(
				copyItems[previous],
				{ autoAlpha: 0, y: -8, duration: 0.3, ease: 'power2.in' },
				0
			);
		}
		if (copyItems[current]) {
			transitionTl.fromTo(
				copyItems[current],
				{ autoAlpha: 0, y: 8 },
				{ autoAlpha: 1, y: 0, duration: 0.45 },
				0.12
			);
		}
	};

	// Rotate the wheel to a date and activate it.
	const goTo = (index, { animate = true } = {}) => {
		const next = clamp(index, 0, count - 1);
		applyActive(next, { animate });

		const rotation = rotationFor(next);
		if (animate && !prefersReducedMotion()) {
			gsap.to(ring, {
				rotation,
				duration: 0.7,
				ease: 'power3.inOut',
				overwrite: true,
			});
		} else {
			gsap.set(ring, { rotation });
		}
	};

	const activateFromInteraction = (index) => {
		setLive(true); // explicit navigation should announce the new date
		stopTimer();
		goTo(index);
		startTimer();
	};

	// Dates: each point is a real button, so Enter/Space work for free.
	items.forEach((item, i) => {
		const point = item.querySelector('.timeline__point');
		if (point) {
			point.addEventListener('click', () => activateFromInteraction(i));
		}
	});

	if (prevBtn) {
		prevBtn.addEventListener('click', () =>
			activateFromInteraction(current - 1)
		);
	}
	if (nextBtn) {
		nextBtn.addEventListener('click', () =>
			activateFromInteraction(current + 1)
		);
	}

	// Keyboard: left/right arrows step the wheel while it has focus.
	if (stage) {
		stage.addEventListener('keydown', (event) => {
			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				activateFromInteraction(current - 1);
			} else if (event.key === 'ArrowRight') {
				event.preventDefault();
				activateFromInteraction(current + 1);
			}
		});

		// Pause auto-advance while the visitor is interacting with the wheel.
		stage.addEventListener('pointerenter', () => {
			isPointerOver = true;
			stopTimer();
		});
		stage.addEventListener('pointerleave', () => {
			isPointerOver = false;
			startTimer();
		});
		stage.addEventListener('focusin', () => {
			isFocused = true;
			stopTimer();
		});
		stage.addEventListener('focusout', () => {
			isFocused = false;
			startTimer();
		});
	}

	const settleDrag = (instance) => {
		isDragging = false;
		current = indexFromRotation(instance.rotation);
		gsap.set(ring, { rotation: rotationFor(current) });
		updateButtons();
		startTimer();
	};

	// Drag / flick to spin, snapping to the nearest date and clamped to the ends.
	const [drag] = Draggable.create(ring, {
		type: 'rotation',
		trigger: stage || ring,
		inertia: !prefersReducedMotion(),
		bounds: { minRotation, maxRotation },
		snap: (value) =>
			clamp(
				Math.round(value / stepDeg) * stepDeg,
				minRotation,
				maxRotation
			),
		onPress() {
			isDragging = true;
			setLive(false); // per-frame drag updates must not announce
			stopTimer();
		},
		onDrag() {
			applyActive(indexFromRotation(this.rotation));
		},
		onThrowUpdate() {
			applyActive(indexFromRotation(this.rotation));
		},
		onThrowComplete() {
			settleDrag(this);
		},
		onRelease() {
			isDragging = false;
			// With inertia the wheel keeps spinning; wait for onThrowComplete.
			if (!this.isThrowing) {
				settleDrag(this);
			}
		},
	});

	desktopMQ.addEventListener('change', () => {
		if (desktopMQ.matches) {
			if (drag) {
				drag.update(true);
			}
			startTimer();
		} else {
			stopTimer();
		}
	});

	goTo(current, { animate: false });
	startTimer();
};

const init = () => {
	document.querySelectorAll(SELECTOR).forEach((root) => initWheel(root));
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
